import { useContext } from 'react';

import { GetPayLinkResponse } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import { ActivationContext } from '../../../activation/Activation';
import { fetchGetSellerPayLink } from '../../../api/lending/edge';
import { useStateKey } from '../../../hooks/useStateKey.hook';
import { examplePayLinksResponse } from '../components/PayLinkTable/examplePaylinks';

export type PayLinkStatus = keyof NonNullable<
  GetPayLinkResponse['meta']['totals_by_status']
>;

export type SellerPayLink = GetPayLinkResponse['data'][0];

type TPayLinkQueryParams = {
  page?: number;
  search?: { searchValue: string; searchFields: string[] };
  isArchived?: boolean;
  status?: PayLinkStatus;
  orderBy?: 'last_updated';
  includeDeleted?: boolean;
};

export const usePayLinks = ({
  page,
  isArchived,
  orderBy,
  searchQuery,
  includeDeleted = false,
}: Omit<TPayLinkQueryParams, 'search'> & {
  searchQuery?: string;
} = {}) => {
  const [searchParams] = useSearchParams();

  const { isOnboarded, isLoading } = useContext(ActivationContext);
  const { stateKey } = useStateKey();
  const status = searchParams.get('status') as PayLinkStatus;
  const search = searchQuery
    ? {
        searchValue: searchQuery,
        searchFields: ['reference', 'business_name'],
      }
    : undefined;
  const query = buildSellerPaylinkSearchParams({
    page: page || 1,
    search,
    isArchived: isArchived ?? false,
    status,
    orderBy,
  });

  const { data, refetch: fetchPayLinks } = useQuery({
    queryKey: ['fetchGetSellerPayLink', query],
    queryFn: () =>
      fetchGetSellerPayLink({
        stateKey: stateKey!,
        query: query,
      }),
    enabled: !isLoading,
    keepPreviousData: true,
  });

  const payLinksResponse = isOnboarded ? data : examplePayLinksResponse;

  let payLinks = payLinksResponse?.data || [];
  if (!includeDeleted)
    payLinks = payLinks.filter((payLinks) => !payLinks.deleted_at);

  return {
    payLinks: payLinks,
    fetchPayLinks,
    paginationLinks: payLinksResponse?.links,
    totalCount: payLinksResponse?.meta?.count,
    statusAggregate: payLinksResponse?.meta?.totals_by_status,
  };
};

export const buildSellerPaylinkSearchParams = ({
  page,
  search,
  isArchived,
  status,
  orderBy,
}: TPayLinkQueryParams) => {
  const params = new URLSearchParams();
  if (search) {
    params.append('search', search.searchValue);
    params.append('search_fields', search.searchFields.join(','));
  }
  if (page && page !== 1) {
    params.append('page', `${page}`);
  }
  if (isArchived !== undefined) params.append('is_archived', `${isArchived}`);
  if (status) params.append('status', status);
  if (orderBy) params.append('order_by', orderBy);

  if (params.getAll.length === 0) return '';

  return `?${params.toString()}`;
};
